import React, { useEffect, useMemo, useRef, useState } from "react";

import { FunctionalMap } from "../utils/objectUtils";

import { DataActions, dataActionsHelper } from "./dataActions";
import { MoveActions, moveActionsHelper } from "./moveActions";
import { SelectionActions, selectionActionsHelper } from "./selectionActions";

export interface DataContextType extends SelectionActions, DataActions, MoveActions {
	containers: FunctionalMap<string, ContainerType>;
	elements: FunctionalMap<string, ElementDefinitionType>;
	elementsInContainers: FunctionalMap<string, ContainerElementRef>;
	containerEditingId?: string;
	elementEditing?: ContainerElementRef;
	displayedElements: Array<string>;
	collisionMode: CollisionModeEnum;
	setCollisionMode: React.Dispatch<React.SetStateAction<CollisionModeEnum>>;
}

export type CollisionModeEnum = "IGNORE" | "MOVE_UP" | "MOVE_PAST" | "BLOCK";
export const COLLISION_MODE_LIST: Array<CollisionModeEnum> = [
	"IGNORE",
	"MOVE_UP",
	"MOVE_PAST",
	"BLOCK"
];

export const DataContext = React.createContext<DataContextType | null>(null);

interface Props {
	children: React.ReactNode;
}

export default function Data({ children }: Props) {
	const elementsMap = useRef<FunctionalMap<string, ElementDefinitionType>>(new FunctionalMap());
	const containersMap = useRef<FunctionalMap<string, ContainerType>>(new FunctionalMap());
	const elementsPositionMap = useRef<FunctionalMap<string, ContainerElementRef>>(
		new FunctionalMap()
	);
	const [displayedElements, setDisplayedElements] = useState<Array<string>>(() => []);
	const [elementEditing, setElementEditing] = useState<ContainerElementRef>();
	const [containerEditingId, setContainerEditingId] = useState<string>();
	const [collisionModeInternal, setCollisionMode] = useState<CollisionModeEnum>("MOVE_UP");
	const collisionMode = useRef<CollisionModeEnum>(collisionModeInternal);
	useEffect(() => {
		// Keep ref and internal state synchronized.
		// Ref is needed for moveActions to NOT depend on state... moveActions MUST be NOT re-computed
		collisionMode.current = collisionModeInternal;
	}, [collisionModeInternal]);

	useEffect(() => {
		if (containerEditingId) {
			setDisplayedElements(
				Array.from(elementsMap.current.values())
					.filter(
						({ id }) =>
							!elementsPositionMap.current.has(id) ||
							elementsPositionMap.current.get(id)?.containerId === containerEditingId
					)
					.map(({ id }) => id)
			);
		} else {
			setDisplayedElements(
				Array.from(elementsMap.current.values())
					.filter(({ id }) => !elementsPositionMap.current.has(id))
					.map(({ id }) => id)
			);
		}
	}, [containerEditingId]);

	const selectionUtils = useMemo<SelectionActions>(
		() =>
			selectionActionsHelper(
				setElementEditing,
				setContainerEditingId,
				elementsPositionMap.current
			),
		[]
	);

	const moveActions = useMemo<MoveActions>(
		() =>
			moveActionsHelper(
				setElementEditing,
				elementsMap.current,
				containersMap.current,
				elementsPositionMap.current,
				// Use the ref itself for collisionMode, as since it is a "primitive Ref", the value itself would be set here, so never updated...
				// This works for the others as they are Objects (Map), so the objects themselves are mutated.
				collisionMode
			),
		[]
	);
	const dataActions = useMemo<DataActions>(
		() =>
			dataActionsHelper(
				setDisplayedElements,
				setElementEditing,
				setContainerEditingId,
				moveActions,
				elementsMap.current,
				containersMap.current,
				elementsPositionMap.current
			),
		[moveActions]
	);

	useEffect(() => {
		// Load data from localStorage on mount
		const localStorageContent = localStorage.getItem("library");
		if (localStorageContent !== null) {
			dataActions.importData(localStorageContent);
		}
	}, [dataActions]);

	useEffect(() => {
		// Save data when it is changed...
		localStorage.setItem("library", dataActions.exportData());
	}, [dataActions, containerEditingId, elementEditing, displayedElements]);

	const contextValue: DataContextType = {
		elements: elementsMap.current,
		containers: containersMap.current,
		elementsInContainers: elementsPositionMap.current,
		displayedElements,
		elementEditing,
		containerEditingId,
		collisionMode: collisionModeInternal,
		setCollisionMode,
		...selectionUtils,
		...dataActions,
		...moveActions
	};

	return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}
